<template>
  <section class="gallery" v-if="getPhotoGalleries">
    <v-toolbar>
      <h1>Photo Gallery Settings</h1>
      <v-spacer />
      <v-btn color="secondary" @click="addGridLayoutHeight">
        Add height <v-icon dark right>add</v-icon>
      </v-btn>
      <v-btn color="success" @click="saveGallery">
        Save changes <v-icon dark right>save</v-icon>
      </v-btn>
      <v-btn color="primary" @click.stop="toggleTabDialog(true)">
        Add new tab <v-icon dark right>add</v-icon>
      </v-btn>
      <v-btn color="secondary" @click="deleteTab">
        Delete current tab <v-icon dark right>clear</v-icon>
      </v-btn>
      <a-upload-section @lastUploadedPhotos="saveDownloadedPhotos"/>
    </v-toolbar>

    <!-- MODAL FOR TABS CREATION -->
    <v-dialog persistent v-model="isTabDialogOpen" max-width="500px">
      <v-card>
        <v-card-title>
          Create New Tab
        </v-card-title>
        <v-card-text>
          <content-field
            label="Name"
            hint="Please, enter tab name"
            v-model="newTabName"
            required
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" flat @click="saveTab">Save</v-btn>
          <v-btn color="primary" flat @click.stop="toggleTabDialog(false)">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- MODAL FOR TABS CREATION -->

    <v-tabs dark show-arrows centered v-model="activeTab">
      <v-tabs-slider color="red"/>
      <v-tab
        v-for="(name, i) in tabs"
        :key="`tab-${i}`"
      >
        {{name}}
      </v-tab>
    </v-tabs>
    <grid-layout
      v-if="getGridLayout"
      :layout="getGridLayout"
      :autoSize="false"
      :style="gridLayoutStyles"
      :col-num="gridColumnNumbers"
      :row-height="gridLayoutRowHeight"
      :is-draggable="true"
      :is-resizable="true"
      :is-mirrored="false"
      :vertical-compact="true"
      :margin="[15, 15]"
      :use-css-transforms="true"
    >
      <grid-item
        v-for="item in getGridLayout"
        class="gallery-photo-item"
        :key="`grid-item-${item.i}`"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
      >
        <div
          :style="setGalleryItemStyle(item.url)"
          class="gallery-photo-item__background-image"
        ></div>
        <v-icon
          @click="deleteImage(item.i)"
          class="gallery-photo-item__close-mark"
          light
          right
        >
          clear
        </v-icon>
      </grid-item>
    </grid-layout>
  </section>
</template>

<script>
  import { windowWidthGetter } from '@/utils/mixins'
  import calculateGridRowHeight from '@/utils/calculateGridRowHeight'
  import GalleriesList from '@/components/pages/Gallery/GalleriesList'
  import AUploadSection from '@/components/shared/AUploadSection'
  import VueGridLayout from 'vue-grid-layout'
  import { mapActions, mapGetters } from 'vuex'
  var GridLayout = VueGridLayout.GridLayout
  var GridItem = VueGridLayout.GridItem

  export default {
    name: 'PhotoGallery',
    components: {
      GridLayout,
      GridItem,
      GalleriesList,
      AUploadSection
    },
    mixins: [windowWidthGetter],
    calculateGridRowHeight,
    data () {
      return {
        galleryList: [],
        activeTab: '0',
        gridColumnNumbers: 12,
        isTabDialogOpen: false,
        isImagesDialogOpen: false,
        isImageGalleryOpen: false,
        newTabName: '',
        hoverEffectList: [
          'Focus In',
          'Focus Out',
          'None'
        ],
        imageData: {
          url: '',
          hoverEffect: 'None',
        },
        gridLayoutHeight: 0
      }
    },
    computed: {
      ...mapGetters([
        'getPhotoGalleries'
      ]),
      tabs () {
        return this.galleryList.map((gallery) => {
          return gallery.name
        })
      },
      getGridLayout () {
        if (this.galleryList.length === 0) return
        const currentGallery = [...this.galleryList[this.activeTab].photoGalleryItems]
        return currentGallery.map((item) => {
          item.metadata.url = item.url
          return item.metadata
        })
      },
      nextElementCoordinates () {
        const lastListItemMetadata = this.getLastListItemMetadata()
        const canElementPutInRow = lastListItemMetadata
          ? lastListItemMetadata.x + lastListItemMetadata.w + 2 > this.gridColumnNumbers
          : null
        switch (true) {
          case !lastListItemMetadata: {
            return {
              x: 0, y: 0, i: 0
            }
          }
          case canElementPutInRow: {
            return {
              x: 0, y: lastListItemMetadata.y + 2, i: +lastListItemMetadata.i + 1
            }
          }
          default: {
            return {
              x: lastListItemMetadata.x + lastListItemMetadata.w, y: lastListItemMetadata.y, i: +lastListItemMetadata.i + 1
            }
          }
        }
      },
      gridLayoutStyles () {
        return {
          height: this.gridLayoutHeight
        }
      },
      gridLayoutRowHeight () {
        return this.$options.calculateGridRowHeight(this.windowWidthGetter_width)
      }
    },
    watch: {
      getPhotoGalleries: {
        immediate: true,
        handler () {
          if (this.getPhotoGalleries) {
            this.galleryList = [...this.getPhotoGalleries]
          }
        }
      }
    },
    methods: {
      ...mapActions([
        'fetchPhotoGalleries',
        'createPhotoGallery',
        'editPhotoGallery',
        'removePhotoGallery'
      ]),
      toggleTabDialog (value) {
        if (!value) {
          this.newTabName = ''
        }
        this.isTabDialogOpen = value
      },
      async saveTab () {
        const payload = {
          name: this.newTabName,
          photoGalleryItems: []
        }
        await this.createPhotoGallery(payload)
        this.toggleTabDialog(false)
      },
      async deleteTab () {
        await this.removePhotoGallery(this.galleryList[this.activeTab].id)
        this.activeTab = this.tabs.length ? '0' : null
      },
      toggleImagesDialog (value) {
        this.isImagesDialogOpen = value
        this.clearImageData()
      },
      getLastListItemMetadata () {
        const currentItemLength = this.galleryList[this.activeTab].photoGalleryItems.length
        if (currentItemLength > 0) {
          return this.galleryList[this.activeTab].photoGalleryItems[currentItemLength - 1].metadata
        }
      },
      saveImage (photoItem) {
        this.galleryList[this.activeTab].photoGalleryItems = [...this.galleryList[this.activeTab].photoGalleryItems, {
          url: photoItem.imagesUrls.originalImageUrl,
          hoverEffect: 'Focus In',
          metadata: {
            x: this.nextElementCoordinates.x,
            y: this.nextElementCoordinates.y,
            w: 2,
            h: 4,
            i: this.nextElementCoordinates.i.toString()
          }
        }]
      },
      toggleGalleryDialog (value) {
        this.isImageGalleryOpen = value
      },
      setImage (url) {
        this.imageData.url = url.imagesUrls.originalImageUrl
        this.toggleGalleryDialog(false)
      },
      setGalleryItemStyle (url) {
        return {
          backgroundImage: `url(${url})`
        }
      },
      async saveGallery () {
        await this.editPhotoGallery(this.galleryList[this.activeTab])
      },
      deleteImage (index) {
        const currentGallery= [...this.galleryList[this.activeTab].photoGalleryItems]
        this.galleryList[this.activeTab].photoGalleryItems = currentGallery.filter((item) => item.metadata.i !== index)
      },
      clearImageData () {
        for (let key in this.imageData) {
          if (this.imageData.hasOwnProperty(key)) {
            this.imageData[key] = ''
          }
        }
      },
      addGridLayoutHeight () {
        const heightNumber = this.gridLayoutHeight.replace(/px/g, '');
        this.gridLayoutHeight = `${+heightNumber + 800}px`
      },
      setGridLayoutStyles () {
        let height = window.innerHeight
          || document.documentElement.clientHeight
          || document.body.clientHeight
        const toolbarHeight = document.querySelector('.toolbar').scrollHeight
        const tabsHeight = document.querySelector('.tabs').scrollHeight
        return height - toolbarHeight - tabsHeight + 'px'
      },
      saveDownloadedPhotos (photos) {
        for (let i = 0; i < photos.length; i++) {
          this.saveImage(photos[i])
        }
      }
    },
    created () {
      this.fetchPhotoGalleries()
    },
    mounted () {
      setTimeout(() => {
        this.gridLayoutHeight = this.setGridLayoutStyles()
      }, 500)
    }
  }
</script>

<style lang="scss" scoped>
  .gallery {
    min-height: calc(100vh - 80px);
  }

  /deep/ .gallery-photo-item {
    border: 2px solid grey;
    position: relative;

    &__close-mark {
      position: absolute;
      top: 0;
      right: 0;
      cursor: pointer;

      &:hover {
        color: red;
      }
    }
  }

  .gallery-photo-item__background-image {
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: cover;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/PhotoGallery/index.vue